import React, { memo, useMemo } from 'react';
import {
  VideoTrack,
  useIsMuted,
  type TrackReference,
  type TrackReferenceOrPlaceholder
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { FaMicrophoneSlash } from 'react-icons/fa';
import { PlayerAvatar } from '../common/PlayerAvatar.js';
import { VideoFrame } from './VideoFrame.js';
import { SpeakingRing } from './SpeakingRing.js';
import { avatarIdOf, displayNameOf, hasLiveVideo } from './track-references.js';

/**
 * Props for {@link ParticipantVideoTile}
 */
interface ParticipantVideoTileProps {
  /**
   * The camera or screen-share reference for this tile. `useTracks(...,
   * { withPlaceholder: true })` yields a placeholder for every participant
   * without a published camera, so this is the one tile per participant even
   * when nobody is on video.
   */
  trackRef: TrackReferenceOrPlaceholder;

  /**
   * This participant's microphone, when it is published *and* subscribed.
   * Drives the speaking ring from the measured audio level (#103). Absent for
   * a screen-share tile, and for a remote microphone that is not subscribed,
   * both of which simply never show the ring.
   */
  microphoneTrackRef?: TrackReference;
}

/**
 * One participant tile: live video when there is any, and otherwise the same
 * {@link PlayerAvatar} the rest of the app uses.
 *
 * Every tile carries a name and a microphone badge regardless of camera
 * state. This is the fix for a six-person call showing five identical black
 * boxes: the name and mute indicator used to be mounted only alongside a
 * video track, so exactly the participants you could not recognise by sight
 * were also the ones with no label.
 *
 * The avatar path is deliberately hand-rolled rather than LiveKit's
 * `ParticipantTile` placeholder: the `--lk-*` theme variables are undefined
 * in this app (issue #80), so the LiveKit placeholder would render
 * transparent, and `PlayerAvatar` matches the rest of the UI anyway.
 */
const ParticipantVideoTileComponent: React.FC<ParticipantVideoTileProps> = ({
  trackRef,
  microphoneTrackRef
}) => {
  const { participant } = trackRef;
  const name = displayNameOf(trackRef);
  const avatar = avatarIdOf(participant.metadata);

  // A placeholder reference is all `useIsMuted` needs: it resolves the
  // publication off the participant and reports muted when there is none.
  const microphoneRef = useMemo(
    () => ({ participant, source: Track.Source.Microphone }),
    [participant]
  );
  const isMicrophoneMuted = useIsMuted(microphoneRef);

  const showVideo = hasLiveVideo(trackRef);
  const isScreenShare = trackRef.source === Track.Source.ScreenShare;

  // One analyser per tile with something to measure: a muted or unsubscribed
  // microphone gets none, and muting tears the running one down.
  const measurableMicrophone =
    microphoneTrackRef?.publication.track && !isMicrophoneMuted ? microphoneTrackRef : undefined;

  return (
    <VideoFrame
      isLocal={participant.isLocal}
      isEmpty={!showVideo}
      indicator={measurableMicrophone && <SpeakingRing microphoneTrackRef={measurableMicrophone} />}
    >
      {showVideo ? (
        <VideoTrack
          trackRef={trackRef as TrackReference}
          // A camera fills the tile and may crop at its edges; a screen share
          // is shown whole, never cropped (#123). Your own camera reads as a
          // mirror, the way every other call app shows it; a screen share
          // must never be flipped.
          className={`w-full h-full ${isScreenShare ? 'object-contain' : 'object-cover'} ${
            participant.isLocal && trackRef.source === Track.Source.Camera ? 'scale-x-[-1]' : ''
          }`}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center">
          <PlayerAvatar username={name} avatar={avatar} size="md" />
        </div>
      )}

      <div className="absolute inset-x-0 bottom-0 flex items-center gap-1 bg-black/60 px-1.5 py-0.5">
        {isMicrophoneMuted && (
          <span role="img" aria-label={`${name} microphone muted`} title="Microphone muted">
            <FaMicrophoneSlash className="text-red-400 text-[10px]" />
          </span>
        )}
        <span
          className="truncate text-[11px] leading-tight text-white"
          data-testid="participant-name"
        >
          {name}
        </span>
      </div>
    </VideoFrame>
  );
};

export const ParticipantVideoTile = memo(ParticipantVideoTileComponent);
ParticipantVideoTile.displayName = 'ParticipantVideoTile';
